import { Injectable, InjectionToken, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NavController } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { LocalNotifications, LocalNotificationsPlugin } from '@capacitor/local-notifications';
import { BackgroundGeolocation, BackgroundGeolocationPlugin } from '@capgo/background-geolocation';
import { firstValueFrom } from 'rxjs';
import { filter, exhaustMap } from 'rxjs/operators';
import { AuthFacade } from '../../auth/state/auth.facade';
import { environment } from '../../../environments/environment';
import type { CheckinSuggestion } from './CheckinSuggestionService';

export interface CheckinGeofence {
	placeId: string;
	placeName: string;
	latitude: number;
	longitude: number;
	radius: number;
}

// The native boundary, swapped for doubles in the spec.
export interface GeofenceNative {
	isNative(): boolean;
	isActive(): Promise<boolean>;
	geolocation: BackgroundGeolocationPlugin;
	notifications: LocalNotificationsPlugin;
}

export const GEOFENCE_NATIVE = new InjectionToken<GeofenceNative>('GeofenceNative', {
	providedIn: 'root',
	factory: () => ({
		isNative: () => Capacitor.isNativePlatform(),
		isActive: async () => (await App.getState()).isActive,
		geolocation: BackgroundGeolocation,
		notifications: LocalNotifications,
	})
});

const OPT_IN_ASKED_KEY = 'geofenceOptInAsked';

// Fase 2 of TODO "Geofencing-check-in": real OS geofences around the nearest places (GET /Checkins/geofences),
// only for users who granted "always" location from the opt-in next to the fase 1 suggestion. Entering one
// feeds the place's position into the normal location flow, so CheckinSuggestionService and the backend's
// 150 m / 6 h rule decide - nothing is duplicated here. Without that permission nothing is registered and
// fase 1 runs exactly as before.
// ponytail: transitions reach JS only while the OS keeps or relaunches the app process; the plugin's native
// POST (url) would also cover a fully killed Android app but needs a long-lived token the API doesn't have.
@Injectable({
	providedIn: 'root'
})
export class CheckinGeofenceService {

	constructor(
		@Inject(GEOFENCE_NATIVE) private native: GeofenceNative,
		private http: HttpClient,
		private authFacade: AuthFacade,
		private navCtrl: NavController) {
		if (!this.native.isNative()) {
			return;
		}

		this.native.geolocation.addListener('geofenceTransition', event => {
			const latitude = event.latitude ?? (event.payload?.latitude as number);
			const longitude = event.longitude ?? (event.payload?.longitude as number);
			// Exit events exist only to keep the OS willing to report the next entry (see refresh).
			if (event.enter && latitude && longitude) {
				this.authFacade.setCoordinates(latitude, longitude);
			}
		});
		this.native.notifications.addListener('localNotificationActionPerformed', action => {
			const placeId = action.notification.extra?.placeId;
			if (placeId) {
				this.navCtrl.navigateForward(`tabs/places/detail/${placeId}`, { queryParams: { checkin: true } });
			}
		});

		// Every fresh position (app start, foreground, a geofence entry) re-centres the regions.
		this.authFacade.location$.pipe(
			filter(location => !!location?.latitude && !!location?.longitude),
			exhaustMap(() => this.refresh().catch(() => undefined))
		).subscribe();
	}

	async isEnabled(): Promise<boolean> {
		if (!this.native.isNative()) {
			return false;
		}
		const status = await this.native.geolocation.checkPermissions();
		// iOS reports 'always', Android 'granted'.
		return status.backgroundLocation === 'always' || status.backgroundLocation === 'granted';
	}

	// Offered once: after the OS dialog was answered (or ignored), asking again would be nagging -
	// and iOS doesn't show the "always" upgrade a second time anyway.
	async canOffer(): Promise<boolean> {
		return this.native.isNative() && !localStorage.getItem(OPT_IN_ASKED_KEY) && !(await this.isEnabled());
	}

	async enable(): Promise<boolean> {
		localStorage.setItem(OPT_IN_ASKED_KEY, new Date().toISOString());
		try {
			await this.native.geolocation.requestPermissions({ permissions: ['location', 'backgroundLocation'] });
			await this.native.notifications.requestPermissions();
		} catch {
			// Refused or unavailable - isEnabled() below reads the real outcome.
		}
		const enabled = await this.isEnabled();
		if (enabled) {
			await this.refresh();
		}
		return enabled;
	}

	// Only the difference is applied: re-adding a region you stand in fires an initial "enter" again.
	async refresh(): Promise<void> {
		if (!(await this.isEnabled())) {
			return;
		}
		const geofences = await firstValueFrom(this.http.get<CheckinGeofence[]>(`${environment.apiHost}checkins/geofences`));
		const wanted = new Set(geofences.map(geofence => geofence.placeId));
		const { regions } = await this.native.geolocation.getMonitoredGeofences();

		// Exits are monitored even though only entries interest us: iOS remembers the last transition per region
		// and drops a repeat of it, so a region that never reports leaving would fire its entry exactly once.
		await this.native.geolocation.setupGeofencing({ requestPermissions: false, backgroundLocation: true, notifyOnEntry: true, notifyOnExit: true });
		for (const identifier of regions.filter(region => !wanted.has(region))) {
			await this.native.geolocation.removeGeofence({ identifier });
		}
		for (const geofence of geofences.filter(geofence => !regions.includes(geofence.placeId))) {
			await this.native.geolocation.addGeofence({
				identifier: geofence.placeId,
				latitude: geofence.latitude,
				longitude: geofence.longitude,
				radius: geofence.radius,
				payload: { latitude: geofence.latitude, longitude: geofence.longitude },
			});
		}
	}

	// The fase 1 suggestion while the app isn't on screen: an alert would wait unseen, a notification doesn't.
	async notify(suggestion: CheckinSuggestion): Promise<boolean> {
		if (!this.native.isNative() || await this.native.isActive()) {
			return false;
		}
		await this.native.notifications.schedule({
			notifications: [{
				id: 7001,
				title: 'Inchecken?',
				body: `Je bent bij ${suggestion.placeName}. Tik om in te checken.`,
				extra: { placeId: suggestion.placeId },
			}]
		});
		return true;
	}
}
